import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, FileText } from "lucide-react";
import { format } from "date-fns";
import { PaginationControls } from "@/components/ui/pagination-controls";

interface LinkedReport {
  id: string;
  reportDate: string;
  location: string | null;
  reportKind: "All Pets" | "Interactions Summary";
  href: string;
}

interface LinkedReportsResponse {
  reports: LinkedReport[];
  total: number;
}

interface LinkedReportsListProps {
  animalId: string;
}

const DEFAULT_PAGE_SIZE = 10;

export function LinkedReportsList({ animalId }: LinkedReportsListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const offset = (currentPage - 1) * pageSize;

  const { data, isLoading } = useQuery<LinkedReportsResponse>({
    queryKey: ["/api/animals", animalId, "reports", { limit: pageSize, offset }],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${animalId}/reports?limit=${pageSize}&offset=${offset}`);
      if (!res.ok) throw new Error("Failed to fetch reports");
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3 p-1">
        <Skeleton className="h-4 w-40" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    );
  }

  const reports = data?.reports ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = offset;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return (
    <div data-testid="linked-reports-container">
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-xs font-medium uppercase tracking-wider flex items-center gap-1">
          <FileText className="w-3 h-3" /> Seen in Reports
        </Label>
        {totalCount > 0 && (
          <Badge variant="secondary" className="text-xs" data-testid="badge-linked-reports-count">
            {totalCount}
          </Badge>
        )}
      </div>

      {totalCount === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center" data-testid="text-no-linked-reports">
          <FileText className="h-4 w-4" />
          No linked reports
        </div>
      ) : (
        <>
          <div className="rounded-md border" data-testid="linked-reports-list">
            {reports.map((report, index) => {
              const isEven = index % 2 === 1;
              return (
                <div
                  key={`${report.reportKind}-${report.id}`}
                  className={isEven ? "bg-muted/30" : ""}
                  data-testid={`linked-report-row-${report.id}`}
                >
                  <div className="flex items-center gap-2 px-3 py-2 hover-elevate group">
                    <span className="text-sm font-medium truncate" data-testid={`text-report-date-${report.id}`}>
                      {report.reportDate
                        ? format(new Date(report.reportDate + "T00:00:00"), "MMM d, yyyy")
                        : "—"}
                    </span>
                    <Badge variant="outline" className="text-xs shrink-0" data-testid={`badge-report-kind-${report.id}`}>
                      {report.reportKind}
                    </Badge>
                    {report.location && (
                      <Badge variant="secondary" className="text-xs shrink-0" data-testid={`text-report-location-${report.id}`}>
                        {report.location}
                      </Badge>
                    )}
                    <div className="flex-1" />
                    <a href={report.href} target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid={`button-view-report-${report.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
          {totalCount > DEFAULT_PAGE_SIZE && (
            <PaginationControls
              currentPage={safePage}
              totalPages={totalPages}
              total={totalCount}
              pageSize={pageSize}
              startIndex={startIndex + 1}
              endIndex={endIndex}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
              itemLabel="reports"
              pageSizeOptions={[10, 25, 50]}
              testIdPrefix="linked-reports"
            />
          )}
        </>
      )}
    </div>
  );
}
